import React, { useEffect, useMemo, useRef, useState } from 'react';

const BETA_NOTICE_ENABLED = (process.env.REACT_APP_SHOW_BETA_NOTICE ?? 'true') !== 'false';

const STORAGE_KEY = 'conistra:beta-notice:v2';
const SNAP_MARGIN = 12;
type DragPosition = {
  left: number;
};

type PersistedState = {
  left: number | null;
};

function getInitialState(): PersistedState {
  if (typeof window === 'undefined') {
    return { left: null };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { left: null };

    const parsed = JSON.parse(raw) as PersistedState & { hidden?: boolean };
    if (!parsed || typeof parsed !== 'object') {
      return { left: null };
    }

    const left = parsed.left == null ? null : Number(parsed.left);
    if (left != null && Number.isNaN(left)) return { left: null };

    return { left: left == null ? null : Math.max(0, left) };
  } catch {
    return { left: null };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function snapHorizontal(left: number): number {
  return Math.round(left / 8) * 8;
}

function BetaNotice() {
  const [{ left }, setState] = useState<PersistedState>(getInitialState);
  const [dragPosition, setDragPosition] = useState<DragPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Session-only: hidden resets on reload so the notice reappears next visit.
  const [hidden, setHidden] = useState(false);
  const noticeRef = useRef<HTMLElement | null>(null);
  const dragPositionRef = useRef<DragPosition | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startLeft: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        left,
      })
    );
  }, [left]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = () => {
      const el = noticeRef.current;
      if (!el) return;
      if (left == null) return;

      const rect = el.getBoundingClientRect();
      const maxLeft = Math.max(SNAP_MARGIN, window.innerWidth - rect.width - SNAP_MARGIN);
      const clampedLeft = clamp(left, SNAP_MARGIN, maxLeft);

      setState((prev) => ({
        ...prev,
        left: clampedLeft,
      }));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [left]);

  const style = useMemo<React.CSSProperties>(() => {
    if (dragPosition) {
      return {
        left: `${dragPosition.left}px`,
        right: 'auto',
      };
    }

    if (left == null) {
      return {};
    }

    return {
      left: `${left}px`,
      right: 'auto',
    };
  }, [dragPosition, left]);

  const setCurrentDragPosition = (position: DragPosition | null) => {
    dragPositionRef.current = position;
    setDragPosition(position);
  };

  const handlePointerDown: React.PointerEventHandler<HTMLElement> = (event) => {
    if (event.button !== 0) return;

    const target = event.target as HTMLElement;
    if (target.closest('[data-no-drag="true"]')) return;

    const el = noticeRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startLeft: rect.left,
      width: rect.width,
    };

    el.setPointerCapture(event.pointerId);
    setCurrentDragPosition({ left: rect.left });
    setIsDragging(true);
  };

  const handlePointerMove: React.PointerEventHandler<HTMLElement> = (event) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const maxLeft = Math.max(SNAP_MARGIN, window.innerWidth - drag.width - SNAP_MARGIN);

    const left = clamp(drag.startLeft + deltaX, SNAP_MARGIN, maxLeft);

    setCurrentDragPosition({ left });
  };

  const finalizeDrag = (pointerId: number) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== pointerId) return;

    const current = dragPositionRef.current ?? { left: drag.startLeft };
    const maxLeft = Math.max(SNAP_MARGIN, window.innerWidth - drag.width - SNAP_MARGIN);
    const snappedLeft = clamp(snapHorizontal(current.left), SNAP_MARGIN, maxLeft);

    dragStateRef.current = null;
    setCurrentDragPosition(null);
    setIsDragging(false);
    setState((prev) => ({ ...prev, left: snappedLeft }));
  };

  const handlePointerUp: React.PointerEventHandler<HTMLElement> = (event) => {
    const el = noticeRef.current;
    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    finalizeDrag(event.pointerId);
  };

  const handlePointerCancel: React.PointerEventHandler<HTMLElement> = (event) => {
    const el = noticeRef.current;
    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    finalizeDrag(event.pointerId);
  };

  if (!BETA_NOTICE_ENABLED) return null;
  if (hidden) return null;

  return (
    <aside
      ref={noticeRef}
      className={`beta-notice ${isDragging ? 'beta-notice--dragging' : ''}`}
      role="note"
      aria-live="polite"
      style={style}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div className="beta-notice__head">
        <div className="beta-notice__badge">BETA</div>
        <button
          type="button"
          className="beta-notice__close"
          data-no-drag="true"
          aria-label="Hide beta notice"
          onClick={() => setHidden(true)}
        >
          ×
        </button>
      </div>
      <p className="beta-notice__title">Thanks for helping shape Conistra.</p>
      <p className="beta-notice__text">
        You are using an early beta version on your own behalf. During beta, refunds are not
        available.
      </p>
      <p className="beta-notice__text">
        Feedback is welcome at{' '}
        <a className="beta-notice__link" href="mailto:feedback@conistra.ai" data-no-drag="true">
          feedback@conistra.ai
        </a>
        .
      </p>
    </aside>
  );
}

export default BetaNotice;
